// Inbox — two-pane triage (design 1e "triage queue with detail pane").
//
// This module is the whole inbox surface: the pure view-model builders,
// the prop-driven presentational pieces (queue rows, the approval-prompt
// card, the reply composer), and the ONE data-wiring component
// (`InboxTriage`) that both `/inbox` and `/inbox/:id` render.
//
// **Redesign, not re-plumb.** Every byte of data comes from queries that
// already existed behind the previous `<AgentInbox>` / `<SessionView>`:
//   - `approvals.list`  → the "Needs you" queue + the selected approval.
//   - `runs.list`       → the "Running" / "Done today" groups AND the
//                          session_id→agent_id lookup for approval rows.
//   - `agents.list`     → agent_id→name (global, few — same pattern as
//                          the Sessions list, whose helpers we reuse).
//   - `runs.byId`       → the selected session's agent / matter / owner.
//   - `whoAmI`          → ownership gate for the decide + reply actions.
//   - `approvals.decide` / `runs.sendMessage` → the two existing writes.
// Nothing new is fetched, and the routing contract (`/inbox`,
// `/inbox/:id`) is unchanged — selection IS the `:id` in the URL, so a
// queue click is a `navigate('/inbox/:id')` and deep links still resolve.
//
// **Honest scope.** The design mock also shows a "While you were away"
// recap timeline, a "Context when you return" chip row, and a
// "times out in 26m" countdown. None of those are backed by data plumbed
// into the inbox today (HITL has no wall-clock timeout at all — see
// CLAUDE.md), so they are deliberately omitted rather than faked.

import './inbox.css';

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { SbIcon } from '../../design/sb-icon.js';
import { ASK_USER_QUESTION_TOOL, bareToolName } from '../../headless/approval-policy.js';
import { trpc } from '../../trpc-client.js';
import { ApprovalFormDataEditor } from '../approvals/form-data-editor.js';
import { sessionStatusLabel, truncateToolName } from '../format.js';
// Reuse the Sessions list's agent-name resolver — global, few agents, one
// `agents.list` query, misses fall back to a truncated id (fn-52.1). Single
// source: never re-implement the same lookup two pages apart.
import { buildAgentNameMap, resolveAgentLabel } from '../runs/list.js';

// ── Wire slices (the fields we read off the BFF rows) ─────────────────
//
// The tRPC-inferred rows carry more; these minimal readonly shapes are
// what the pure builders below accept so they unit-test without tRPC.

export interface SessionRowLike {
  readonly id: string;
  readonly agent_id: string;
  /** The agent version this session is PINNED to (may trail the current one). */
  readonly agent_version_id?: string;
  readonly matter_id: string | null;
  readonly status: string;
  readonly created_at: string;
  readonly last_updated_at: string;
  readonly terminal_at: string | null;
}

export type AckKind =
  | 'tool_confirmation'
  | 'custom_tool_result_immediate'
  | 'custom_tool_result_deferred';

export interface ApprovalRowLike {
  readonly id: string;
  readonly session_id: string;
  readonly ack_kind: AckKind;
  readonly tool_kind: 'builtin' | 'mcp' | 'custom';
  readonly tool_name: string;
  readonly tool_args: Record<string, unknown>;
  readonly created_at: string;
}

// ── Pure helpers (exported for unit tests) ────────────────────────────

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Compact age like "just now" / "4m" / "2h" / "3d". Echoes the invalid input. */
export function formatRelativeAge(iso: string, nowMs: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  const delta = nowMs - t;
  if (delta < MINUTE) return 'just now';
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m`;
  if (delta < DAY) return `${Math.floor(delta / HOUR)}h`;
  return `${Math.floor(delta / DAY)}d`;
}

/** Short time-of-day like "9:42 am" — used for "Done today" rows. */
export function formatShortClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** True when `ms` falls on the same local calendar day as `nowMs`. */
export function isSameLocalDay(ms: number, nowMs: number): boolean {
  const a = new Date(ms);
  const b = new Date(nowMs);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'completed',
  'failed',
  'cancelled',
  'expired',
]);

export function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

export interface SessionBuckets {
  readonly running: readonly SessionRowLike[];
  readonly doneToday: readonly SessionRowLike[];
}

/**
 * Split recent sessions into the two design groups: `running` (live) and
 * `doneToday` (terminal AND finished on the current local day). Both are
 * sorted most-recent-first. Sessions that finished on a prior day fall out
 * — "Done today" is a triage horizon, not the full history (that's the
 * Sessions page). `terminal_at` wins over `last_updated_at` for the "when
 * did it finish" test since a reconcile sweep can bump `last_updated_at`.
 */
export function bucketSessions(
  rows: readonly SessionRowLike[],
  nowMs: number = Date.now(),
): SessionBuckets {
  const running: SessionRowLike[] = [];
  const doneToday: SessionRowLike[] = [];
  for (const row of rows) {
    if (row.status === 'running') {
      running.push(row);
      continue;
    }
    if (isTerminalStatus(row.status)) {
      const doneIso = row.terminal_at ?? row.last_updated_at;
      const t = new Date(doneIso).getTime();
      if (Number.isFinite(t) && isSameLocalDay(t, nowMs)) doneToday.push(row);
    }
  }
  const byRecency = (a: SessionRowLike, b: SessionRowLike): number =>
    new Date(b.last_updated_at).getTime() - new Date(a.last_updated_at).getTime();
  running.sort(byRecency);
  doneToday.sort(byRecency);
  return { running, doneToday };
}

// Arg keys that make a good single-line summary, in priority order. Falls
// back to compact JSON so the row never renders blank.
const SUMMARY_ARG_KEYS: readonly string[] = [
  'command',
  'cmd',
  'query',
  'path',
  'url',
  'message',
  'content',
  'text',
  'name',
  'title',
];

/** One-line human summary of a tool call's arguments for the queue row. */
export function approvalArgsSummary(toolArgs: Record<string, unknown>): string {
  for (const key of SUMMARY_ARG_KEYS) {
    const v = toolArgs[key];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  const keys = Object.keys(toolArgs);
  if (keys.length === 0) return 'No arguments';
  try {
    return JSON.stringify(toolArgs);
  } catch {
    return keys.join(', ');
  }
}

// ── Queue view-models ─────────────────────────────────────────────────

export type QueueMarker =
  // A coloured status dot (`brand` pulses for live work).
  | { readonly kind: 'dot'; readonly tone: 'orange' | 'red' | 'brand' }
  // A terminal-state glyph (check / warning) tinted by tone.
  | { readonly kind: 'icon'; readonly name: string; readonly tone: 'success' | 'danger' | 'muted' };

export interface QueueItemVM {
  /** Stable React key (approval id for approvals, session id otherwise). */
  readonly key: string;
  /** Navigation + selection target (always a session id). */
  readonly sessionId: string;
  readonly marker: QueueMarker;
  /** Primary line — the resolved agent name. */
  readonly heading: string;
  /** Right-aligned age / time. */
  readonly age: string;
  /** Optional bold second line (approval rows). */
  readonly title?: string;
  /** Optional dim third line. */
  readonly subtitle?: string;
}

function agentHeadingForSession(
  sessionId: string,
  sessionMap: ReadonlyMap<string, SessionRowLike>,
  agentNames: ReadonlyMap<string, string>,
): string {
  const row = sessionMap.get(sessionId);
  if (row !== undefined) return resolveAgentLabel(row.agent_id, agentNames);
  // Approval whose session is outside the recent runs page — the session
  // is active (awaiting_approval) so this is rare; fall back to its id.
  return `Session ${sessionId.slice(0, 8)}…`;
}

export function buildApprovalQueueItems(
  approvals: readonly ApprovalRowLike[],
  sessionMap: ReadonlyMap<string, SessionRowLike>,
  agentNames: ReadonlyMap<string, string>,
  nowMs: number = Date.now(),
): QueueItemVM[] {
  return approvals.map((a) => ({
    key: a.id,
    sessionId: a.session_id,
    marker: { kind: 'dot', tone: 'orange' },
    heading: agentHeadingForSession(a.session_id, sessionMap, agentNames),
    age: formatRelativeAge(a.created_at, nowMs),
    title: `Approval — ${truncateToolName(a.tool_name, 32)}`,
    subtitle: approvalArgsSummary(a.tool_args),
  }));
}

export function buildRunningQueueItems(
  rows: readonly SessionRowLike[],
  agentNames: ReadonlyMap<string, string>,
  nowMs: number = Date.now(),
): QueueItemVM[] {
  return rows.map((r) => ({
    key: r.id,
    sessionId: r.id,
    marker: { kind: 'dot', tone: 'brand' },
    heading: resolveAgentLabel(r.agent_id, agentNames),
    age: formatRelativeAge(r.last_updated_at, nowMs),
    subtitle:
      r.matter_id !== null ? `Matter ${r.matter_id.slice(0, 8)}…` : `Session ${r.id.slice(0, 8)}…`,
  }));
}

function doneMarker(status: string): QueueMarker {
  if (status === 'completed') return { kind: 'icon', name: 'circle-check-solid', tone: 'success' };
  if (status === 'failed') {
    return { kind: 'icon', name: 'diamond-exclamation-solid', tone: 'danger' };
  }
  // cancelled / expired — finished, but neither a success nor a failure.
  return { kind: 'icon', name: 'circle-check-solid', tone: 'muted' };
}

export function buildDoneQueueItems(
  rows: readonly SessionRowLike[],
  agentNames: ReadonlyMap<string, string>,
): QueueItemVM[] {
  return rows.map((r) => {
    const doneIso = r.terminal_at ?? r.last_updated_at;
    return {
      key: r.id,
      sessionId: r.id,
      marker: doneMarker(r.status),
      heading: resolveAgentLabel(r.agent_id, agentNames),
      age: formatShortClock(doneIso),
      subtitle: sessionStatusLabel(r.status),
    };
  });
}

// ── Presentational: the queue (left pane) ─────────────────────────────

const MARKER_ICON_COLOR: Record<'success' | 'danger' | 'muted', string> = {
  success: 'var(--color-text-success)',
  danger: 'var(--color-background-danger)',
  muted: 'var(--color-icon-subtle)',
};

function QueueMarkerView({ marker }: { readonly marker: QueueMarker }): React.ReactElement {
  if (marker.kind === 'dot') {
    return (
      <span
        className={`inbox-row__dot inbox-row__dot--${marker.tone}`}
        data-testid="queue-marker-dot"
        data-tone={marker.tone}
      />
    );
  }
  return <SbIcon name={marker.name} size={14} style={{ color: MARKER_ICON_COLOR[marker.tone] }} />;
}

export interface QueueRowProps {
  readonly item: QueueItemVM;
  readonly selected: boolean;
  readonly onSelect: (sessionId: string) => void;
}

export function QueueRow(props: QueueRowProps): React.ReactElement {
  const { item } = props;
  return (
    <button
      type="button"
      className={`inbox-row${props.selected ? ' inbox-row--selected' : ''}`}
      data-session-id={item.sessionId}
      aria-current={props.selected ? 'true' : undefined}
      onClick={() => props.onSelect(item.sessionId)}
    >
      <span className="inbox-row__marker">
        <QueueMarkerView marker={item.marker} />
      </span>
      <span className="inbox-row__main">
        <span className="inbox-row__head">
          <span className="inbox-row__agent">{item.heading}</span>
          <span className="inbox-row__age caption">{item.age}</span>
        </span>
        {item.title !== undefined && <span className="inbox-row__title">{item.title}</span>}
        {item.subtitle !== undefined && <span className="inbox-row__sub">{item.subtitle}</span>}
      </span>
    </button>
  );
}

export interface QueueGroupProps {
  readonly label: string;
  readonly items: readonly QueueItemVM[];
  readonly emptyCopy: string;
  readonly selectedSessionId: string | null;
  readonly onSelect: (sessionId: string) => void;
}

export function QueueGroup(props: QueueGroupProps): React.ReactElement {
  return (
    <section aria-label={props.label} data-group={props.label}>
      <div className="inbox-group-label caption-semibold">
        {props.label} · {props.items.length}
      </div>
      {props.items.length === 0 ? (
        <p className="inbox-group-empty">{props.emptyCopy}</p>
      ) : (
        props.items.map((item) => (
          <QueueRow
            key={item.key}
            item={item}
            selected={item.sessionId === props.selectedSessionId}
            onSelect={props.onSelect}
          />
        ))
      )}
    </section>
  );
}

export type InboxTab = 'needs-you' | 'all';

export interface InboxQueueProps {
  readonly tab: InboxTab;
  readonly needsYou: readonly QueueItemVM[];
  readonly running: readonly QueueItemVM[];
  readonly doneToday: readonly QueueItemVM[];
  readonly needsYouEmptyCopy: string;
  readonly sessionsEmptyCopy: string;
  readonly selectedSessionId: string | null;
  readonly onTabChange: (tab: InboxTab) => void;
  readonly onSelect: (sessionId: string) => void;
}

export function InboxQueue(props: InboxQueueProps): React.ReactElement {
  return (
    <div className="card inbox-pane inbox-queue">
      <div className="inbox-queue__head">
        <h2>Inbox</h2>
        {/* fieldset (not div+role) per a11y/useSemanticElements; the
            default fieldset chrome is reset in inbox.css. */}
        <fieldset className="seg inbox-queue__seg" aria-label="Inbox filter">
          <button
            type="button"
            className="seg__item"
            aria-pressed={props.tab === 'needs-you'}
            onClick={() => props.onTabChange('needs-you')}
          >
            Needs you
          </button>
          <button
            type="button"
            className="seg__item"
            aria-pressed={props.tab === 'all'}
            onClick={() => props.onTabChange('all')}
          >
            All
          </button>
        </fieldset>
      </div>
      <div className="inbox-queue__scroll">
        <QueueGroup
          label="Needs you"
          items={props.needsYou}
          emptyCopy={props.needsYouEmptyCopy}
          selectedSessionId={props.selectedSessionId}
          onSelect={props.onSelect}
        />
        {props.tab === 'all' && (
          <>
            <QueueGroup
              label="Running"
              items={props.running}
              emptyCopy={props.sessionsEmptyCopy}
              selectedSessionId={props.selectedSessionId}
              onSelect={props.onSelect}
            />
            <QueueGroup
              label="Done today"
              items={props.doneToday}
              emptyCopy={props.sessionsEmptyCopy}
              selectedSessionId={props.selectedSessionId}
              onSelect={props.onSelect}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ── Presentational: the detail pane (right) ───────────────────────────

export interface InboxDetailHeaderProps {
  readonly agentLabel: string;
  readonly needsApproval: boolean;
  readonly matterId: string | null;
  readonly sessionId: string;
  /** e.g. "Requested 4m ago" — omitted (null) for non-approval sessions. */
  readonly requestedLabel: string | null;
  readonly openSessionHref: string;
}

export function InboxDetailHeader(props: InboxDetailHeaderProps): React.ReactElement {
  return (
    <div className="inbox-detail__head">
      <div className="inbox-detail__title-row">
        <h3 className="inbox-detail__title">
          {props.needsApproval ? `${props.agentLabel} needs your approval` : props.agentLabel}
        </h3>
        {props.needsApproval && <span className="tag tag--orange">Needs you</span>}
        <div className="inbox-detail__title-actions">
          {/* Open session → the full transcript on the Sessions page,
              matching the existing `/runs/:id` link pattern. */}
          <Link className="btn btn--secondary btn--xs" to={props.openSessionHref}>
            <SbIcon name="external-link" size={12} />
            Open session
          </Link>
        </div>
      </div>
      <div className="inbox-detail__meta">
        {props.matterId !== null && (
          <span className="tag tag--blue">
            <SbIcon name="briefcase" size={11} />
            {props.matterId.slice(0, 8)}…
          </span>
        )}
        <span className="tag tag--grey" style={{ fontFamily: 'var(--font-family-mono)' }}>
          Session #{props.sessionId.slice(0, 8)}
        </span>
        {props.requestedLabel !== null && <span className="caption">{props.requestedLabel}</span>}
      </div>
    </div>
  );
}

export interface ApprovalPromptCardProps {
  readonly toolKind: 'builtin' | 'mcp' | 'custom';
  readonly toolName: string;
  readonly toolArgs: Record<string, unknown>;
  readonly ackKind: AckKind;
  /** The agent's `config_snapshot` — source of the tool declaration. */
  readonly agentConfig?: Record<string, unknown> | null;
  /** Session the approval belongs to — links an elicitation to its form. */
  readonly sessionId?: string;
  /** Tool declaration still loading — holds ONLY the payload-editor Approve. */
  readonly configPending?: boolean;
  /** Caller owns the session → the decide buttons are live. */
  readonly owned: boolean;
  readonly busy: boolean;
  /** Non-null once decided this session — renders a confirmation chip. */
  readonly decision: 'approved' | 'rejected' | null;
  readonly errorMessage: string | null;
  readonly onApprove: (formData?: Record<string, unknown>) => void;
  readonly onDeny: () => void;
}

export function ApprovalPromptCard(props: ApprovalPromptCardProps): React.ReactElement {
  // Immediate-ack rows REQUIRE a `form_data` JSON object on approve (the
  // CP 400s without one), so they get the editable payload box instead
  // of a bare Approve. Deny never needs a payload. Mirrors the Approvals
  // list + run-detail gates.
  //
  // `ask_user_question` is the ONE immediate tool whose ack is not the
  // input echoed back — it takes `{questions, answers}`, a shape its
  // `input_schema` cannot describe and this page has no renderer for.
  // Showing the payload box would hand the operator an Approve that
  // 400s with no way to derive the answers, so route to the session
  // page, which renders the real question form.
  const isElicitation = bareToolName(props.toolName) === ASK_USER_QUESTION_TOOL;
  const needsFormData = props.ackKind === 'custom_tool_result_immediate' && !isElicitation;
  const denyButton = (
    <button
      type="button"
      className="btn btn--tertiary"
      disabled={props.busy}
      onClick={props.onDeny}
    >
      Deny
    </button>
  );
  return (
    <div className="inbox-approval">
      <div className="inbox-approval__head">
        <SbIcon
          name="diamond-exclamation-solid"
          size={18}
          style={{ color: 'var(--color-text-warning)' }}
        />
        <span>Approve this action?</span>
      </div>
      <div className="inbox-approval__tool">
        {props.toolKind} · <strong>{props.toolName}</strong>
      </div>
      {/* For a form-data row the editor below shows the same payload,
          editable — no point rendering it twice. */}
      {!needsFormData && (
        <pre className="inbox-approval__pre">{JSON.stringify(props.toolArgs, null, 2)}</pre>
      )}
      {props.decision !== null ? (
        <div className={`inbox-approval__decided inbox-approval__decided--${props.decision}`}>
          <SbIcon
            name={
              props.decision === 'approved' ? 'circle-check-solid' : 'diamond-exclamation-solid'
            }
            size={14}
          />
          {props.decision === 'approved' ? 'Approved' : 'Denied'}
        </div>
      ) : !props.owned ? (
        <p className="inbox-approval__note">
          Read-only — decide on the <Link to="/approvals">Approvals</Link> page.
        </p>
      ) : isElicitation && props.ackKind === 'custom_tool_result_immediate' ? (
        <div className="inbox-approval__actions">
          <span className="inbox-approval__note" data-testid="inbox-elicitation-note">
            The agent is asking a question —{' '}
            {props.sessionId !== undefined ? (
              <Link to={`/runs/${encodeURIComponent(props.sessionId)}`}>
                answer it on the session
              </Link>
            ) : (
              'answer it on the session'
            )}
            .
          </span>
          {denyButton}
        </div>
      ) : needsFormData ? (
        <ApprovalFormDataEditor
          toolName={props.toolName}
          toolArgs={props.toolArgs}
          agentConfig={props.agentConfig ?? null}
          busy={props.busy || props.configPending === true}
          approveLabel={props.busy ? 'Working…' : 'Approve'}
          onApprove={props.onApprove}
        >
          {denyButton}
        </ApprovalFormDataEditor>
      ) : (
        <div className="inbox-approval__actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={props.busy}
            onClick={() => props.onApprove()}
          >
            {props.busy ? 'Working…' : 'Approve'}
          </button>
          {denyButton}
        </div>
      )}
      {props.errorMessage !== null && (
        <p className="inbox-approval__error" role="alert">
          {props.errorMessage}
        </p>
      )}
    </div>
  );
}

export interface ReplyComposerProps {
  readonly agentLabel: string;
  readonly value: string;
  readonly busy: boolean;
  readonly errorMessage: string | null;
  readonly onChange: (value: string) => void;
  readonly onSend: () => void;
}

export function ReplyComposer(props: ReplyComposerProps): React.ReactElement {
  const canSend = !props.busy && props.value.trim().length > 0;
  return (
    <div className="inbox-composer">
      <input
        className="inbox-composer__input"
        placeholder={`Reply to ${props.agentLabel}`}
        aria-label={`Reply to ${props.agentLabel}`}
        value={props.value}
        disabled={props.busy}
        onChange={(e) => props.onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && canSend) props.onSend();
        }}
      />
      <button
        type="button"
        className="btn btn--primary inbox-composer__send"
        aria-label="Send reply"
        disabled={!canSend}
        onClick={props.onSend}
      >
        <SbIcon name="paper-plane-top" size={15} />
      </button>
      {props.errorMessage !== null && (
        <p className="inbox-approval__error" role="alert">
          {props.errorMessage}
        </p>
      )}
    </div>
  );
}

/** Right-pane placeholder when nothing is selected. */
export function InboxEmptyDetail(): React.ReactElement {
  return (
    <div className="card inbox-pane">
      <div className="inbox-empty">
        <SbIcon name="comment" size={28} style={{ color: 'var(--color-icon-subtle)' }} />
        <p className="inbox-empty__title">Nothing selected</p>
        <p>Pick an item from the queue to review it here.</p>
      </div>
    </div>
  );
}

/** Right-pane message pane (loading / error / no-approval). */
function InboxMessageDetail(props: {
  readonly title: string;
  readonly body: string;
  readonly busy?: boolean;
}): React.ReactElement {
  return (
    <div className="card inbox-pane" aria-busy={props.busy === true ? true : undefined}>
      <div className="inbox-empty">
        <p className="inbox-empty__title">{props.title}</p>
        <p>{props.body}</p>
      </div>
    </div>
  );
}

// ── Data-wiring component ─────────────────────────────────────────────

const POLL_MS = 5_000;
const RUNS_PAGE = 100;
const AGENTS_LIMIT = 200;

export interface InboxTriageProps {
  /** Selected session id from `/inbox/:id`; absent on `/inbox`. */
  readonly selectedSessionId?: string;
}

export function InboxTriage(props: InboxTriageProps): React.ReactElement {
  const navigate = useNavigate();
  const [tab, setTab] = useState<InboxTab>('needs-you');
  const selectedSessionId = props.selectedSessionId ?? null;

  // ── Queries (all pre-existing behind the old package components) ──
  const approvalsQuery = trpc.approvals.list.useQuery({}, { refetchInterval: POLL_MS });
  const runsQuery = trpc.runs.list.useQuery({ limit: RUNS_PAGE }, { refetchInterval: POLL_MS });
  const agentsQuery = trpc.agents.list.useQuery({ limit: AGENTS_LIMIT });
  const whoAmI = trpc.whoAmI.useQuery(undefined, { retry: false, staleTime: 5 * 60 * 1000 });
  // Same cadence as the queue polls — without it the right pane's
  // owned/composer state can go stale after a decision or send.
  const sessionQuery = trpc.runs.byId.useQuery(
    { sessionId: selectedSessionId ?? '' },
    { enabled: selectedSessionId !== null, refetchInterval: POLL_MS },
  );

  const utils = trpc.useUtils();
  const decideMutation = trpc.approvals.decide.useMutation();
  const sendMutation = trpc.runs.sendMessage.useMutation();

  // The decided chip belongs to ONE approval, not to the session: after
  // approving A the agent resumes and can raise B in the SAME session, and
  // a session-scoped decision would render B as already-decided and
  // un-actionable. Track which approval the decision was for.
  const [decision, setDecision] = useState<{
    readonly approvalId: string;
    readonly value: 'approved' | 'rejected';
  } | null>(null);
  const [replyText, setReplyText] = useState('');
  // Reset per-selection local state when the selected session changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only the id may trigger a reset — `mutation.reset` is referentially unstable per render, and adding it would clear in-flight decide/send state on every poll tick.
  useEffect(() => {
    setDecision(null);
    setReplyText('');
    decideMutation.reset();
    sendMutation.reset();
  }, [selectedSessionId]);

  const approvals = (approvalsQuery.data?.data ?? []) as readonly ApprovalRowLike[];
  const runs = (runsQuery.data?.data ?? []) as readonly SessionRowLike[];

  const agentNames = useMemo(
    () => buildAgentNameMap(agentsQuery.data?.data ?? []),
    [agentsQuery.data],
  );
  const sessionMap = useMemo(() => {
    const map = new Map<string, SessionRowLike>();
    for (const row of runs) map.set(row.id, row);
    return map;
  }, [runs]);
  const buckets = useMemo(() => bucketSessions(runs), [runs]);

  const needsYou = useMemo(
    () => buildApprovalQueueItems(approvals, sessionMap, agentNames),
    [approvals, sessionMap, agentNames],
  );
  const running = useMemo(
    () => buildRunningQueueItems(buckets.running, agentNames),
    [buckets.running, agentNames],
  );
  const doneToday = useMemo(
    () => buildDoneQueueItems(buckets.doneToday, agentNames),
    [buckets.doneToday, agentNames],
  );

  const needsYouEmptyCopy = approvalsQuery.isPending
    ? 'Loading…'
    : approvalsQuery.error !== null
      ? `Couldn't load approvals: ${approvalsQuery.error.message}`
      : 'No items need you.';
  const sessionsEmptyCopy = runsQuery.isPending
    ? 'Loading…'
    : runsQuery.error !== null
      ? `Couldn't load sessions: ${runsQuery.error.message}`
      : 'Nothing here.';

  const onSelect = (sessionId: string): void => {
    navigate(`/inbox/${encodeURIComponent(sessionId)}`);
  };

  // ── Right-pane derivation ──
  const sessionRow = sessionQuery.data as SessionRowLike | undefined;
  // The tool declaration comes from the SELECTED session's agent, fetched
  // by id — NOT scanned out of `agents.list`, which is one capped page and
  // ignores `next_cursor`: an agent past that page would silently yield no
  // declaration and skip schema validation. (`agents.list` still backs the
  // name map, where a miss degrades to a truncated id and is harmless.)
  const selectedAgentQuery = trpc.agents.byId.useQuery(
    { agentId: sessionRow?.agent_id ?? '' },
    { enabled: sessionRow !== undefined && sessionRow.agent_id.length > 0 },
  );
  // The agent projection carries the CURRENT version's snapshot, while the
  // session is pinned to `agent_version_id`. Redeploy an agent mid-session
  // and the two diverge — validating a v1 gate against v2's schema could
  // block a valid payload or coax the operator into a v2-shaped one. There
  // is no by-version read endpoint, so use the config ONLY while the pins
  // agree (both present, as strings) and fall back to the generic
  // JSON-object check otherwise.
  const pinnedAgentConfig = useMemo(() => {
    const agent = selectedAgentQuery.data as
      | { current_version_id?: unknown; config?: unknown }
      | undefined;
    if (agent === undefined || sessionRow === undefined) return null;
    const versionId = sessionRow.agent_version_id;
    if (typeof versionId !== 'string' || agent.current_version_id !== versionId) return null;
    return (agent.config ?? null) as Record<string, unknown> | null;
  }, [selectedAgentQuery.data, sessionRow]);
  const selectedApproval =
    selectedSessionId !== null
      ? (approvals.find((a) => a.session_id === selectedSessionId) ?? null)
      : null;
  const owned =
    whoAmI.data !== undefined &&
    sessionQuery.data !== undefined &&
    (sessionQuery.data as { user_id?: string }).user_id === whoAmI.data.userId;
  const agentLabel =
    sessionRow !== undefined
      ? resolveAgentLabel(sessionRow.agent_id, agentNames)
      : selectedSessionId !== null
        ? `Session ${selectedSessionId.slice(0, 8)}…`
        : '';
  const terminal = sessionRow !== undefined && isTerminalStatus(sessionRow.status);

  // `formData` is supplied by the card's editor for immediate-ack rows
  // (the CP requires it there); every other gate decides without one.
  const handleDecide = async (
    d: 'approved' | 'rejected',
    formData?: Record<string, unknown>,
  ): Promise<void> => {
    if (selectedApproval === null || selectedSessionId === null) return;
    try {
      await decideMutation.mutateAsync({
        sessionId: selectedSessionId,
        approvalId: selectedApproval.id,
        decision: d,
        ...(formData !== undefined ? { formData } : {}),
      });
      setDecision({ approvalId: selectedApproval.id, value: d });
      // Drop the row from the queue + refresh the sidebar badge count.
      await Promise.all([
        utils.approvals.list.invalidate(),
        utils.approvals.pendingCount.invalidate(),
      ]);
    } catch {
      // Surfaces via decideMutation.error.
    }
  };

  const handleSend = async (): Promise<void> => {
    const content = replyText.trim();
    if (content.length === 0 || selectedSessionId === null) return;
    try {
      await sendMutation.mutateAsync({ sessionId: selectedSessionId, content });
      setReplyText('');
    } catch {
      // Surfaces via sendMutation.error.
    }
  };

  const detail = ((): React.ReactElement => {
    if (selectedSessionId === null) return <InboxEmptyDetail />;
    // Wait for BOTH the session row + whoAmI so the decide affordances
    // never flash editable↔read-only (mirrors the old detail page).
    if (sessionQuery.isPending || whoAmI.isPending) {
      return <InboxMessageDetail title="Loading…" body="Fetching the session." busy />;
    }
    // Session row unavailable AND no approval to act on → nothing to show.
    if (sessionRow === undefined && selectedApproval === null) {
      return (
        <InboxMessageDetail
          title="Couldn't load this session"
          body="It may have been archived, or you may not have access. Try the Sessions page."
        />
      );
    }
    const openSessionHref = `/runs/${encodeURIComponent(selectedSessionId)}`;
    const showComposer = owned && sessionRow !== undefined && !terminal;
    return (
      <div className="card inbox-pane inbox-detail">
        <InboxDetailHeader
          agentLabel={agentLabel}
          needsApproval={selectedApproval !== null}
          matterId={sessionRow?.matter_id ?? null}
          sessionId={selectedSessionId}
          requestedLabel={
            selectedApproval !== null
              ? `Requested ${formatRelativeAge(selectedApproval.created_at)} ago`
              : null
          }
          openSessionHref={openSessionHref}
        />
        <div className="inbox-detail__body">
          {selectedApproval !== null ? (
            <ApprovalPromptCard
              // Keyed by approval id: the editor seeds its textarea ONCE,
              // so a card reused across a selection change (or a poll that
              // swaps the row in place) would keep approval A's edited JSON
              // while the decide call targets approval B. Remount instead.
              key={selectedApproval.id}
              toolKind={selectedApproval.tool_kind}
              toolName={selectedApproval.tool_name}
              toolArgs={selectedApproval.tool_args}
              ackKind={selectedApproval.ack_kind}
              agentConfig={pinnedAgentConfig}
              {...(selectedSessionId !== null ? { sessionId: selectedSessionId } : {})}
              owned={owned}
              busy={decideMutation.isPending}
              // Separate from `busy` on purpose: this holds ONLY the
              // payload editor's Approve until the tool declaration lands.
              // Folding it into `busy` would disable Deny and ordinary
              // approvals too, stranding the operator while an unrelated
              // query is slow or retrying. Only "pending" while a
              // declaration is actually on its way — a DISABLED query
              // (no session row yet) reports `pending` forever.
              configPending={sessionRow !== undefined && selectedAgentQuery.isPending}
              decision={decision?.approvalId === selectedApproval.id ? decision.value : null}
              errorMessage={decideMutation.error?.message ?? null}
              onApprove={(formData) => void handleDecide('approved', formData)}
              onDeny={() => void handleDecide('rejected')}
            />
          ) : (
            <p className="inbox-detail__note">
              No pending approval for this session. Open the session to see the full transcript.
            </p>
          )}
        </div>
        {showComposer && (
          <ReplyComposer
            agentLabel={agentLabel}
            value={replyText}
            busy={sendMutation.isPending}
            errorMessage={sendMutation.error?.message ?? null}
            onChange={setReplyText}
            onSend={() => void handleSend()}
          />
        )}
      </div>
    );
  })();

  return (
    // `page-inbox` is the stable namespaced hook (tests / e2e selectors);
    // `inbox-root` carries the two-pane grid layout. Deliberately NOT the
    // old `.page` wrapper class — the shell's <main class="page"> already
    // provides it and nesting a second one would double the padding.
    <section className="page-inbox inbox-root">
      <InboxQueue
        tab={tab}
        needsYou={needsYou}
        running={running}
        doneToday={doneToday}
        needsYouEmptyCopy={needsYouEmptyCopy}
        sessionsEmptyCopy={sessionsEmptyCopy}
        selectedSessionId={selectedSessionId}
        onTabChange={setTab}
        onSelect={onSelect}
      />
      {detail}
    </section>
  );
}
